import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { getUrlParamsFetch, handleAxiosError } from '@/shared';
import { urlBackend } from '@/shared/constants';
import {
  AlertSeverityEnum,

  UseFetchEnabledParams,
  UseMutationsParams,
} from '@/shared/interfaces';
import { useUiStore } from '@/store/ui';
import { erpApi } from '@/shared/axios/erp-api';
import { RadioBase, RadioBasePagingRes } from '@/shared/interfaces/erp/mantenimiento-operaciones';
import { isAxiosError } from 'axios';

const { get, post, put } = erpApi();

///* tanSctack query

export const useFetchRadioBases = ({
  page = 1,
  page_size = 10,
  codigo = '',
  nombre = '',
}: GetRadioBaseParams) => {
  return useQuery({
    queryKey: ['radioBase', page, nombre, codigo, page_size],
    queryFn: async () => {
      return getRadioBases({ page, nombre, codigo, page_size });
    },
  });
};

export const useFetchAllRadioBase = ({
  enabled,
  params,
}: UseFetchEnabledParams<GetRadioBaseParams> = {}) => {
  return useQuery({
    queryKey: ['radioBase', ...Object.values(params ?? {})],
    queryFn: () => getRadioBases(params),
    enabled,
  });
};

export const useGetRadioBase = (id: string) => {
  return useQuery({
    queryKey: ['radioBase', id],
    queryFn: async () => {
      return getRadioBase(id);
    },
  });
};

export const useCreateRadioBase = ({
  navigate,

  enableRedirect = true,
  returnUrl,

  returnErrorUrl,
  enableErrorRedirect = false,
  customMessageErrorAlert,

  enableSuccessAlert = true,
  customSuccessMessage,
}: UseMutationsParams) => {
  const queryClient = useQueryClient();
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  const setAlert = useUiStore.getState().setAlert;

  return useMutation({
    mutationFn: createRadioBase,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['radio-base'] });

      enableRedirect && navigate && returnUrl && navigate(returnUrl);

      enableSuccessAlert &&
        setAlert &&
        setAlert(
          true,
          customSuccessMessage ?? 'Radio Base creada correctamente',
          AlertSeverityEnum.success
        );
    },
    onError: error => {
      enableErrorRedirect &&
        navigate &&
        returnUrl &&
        navigate(returnErrorUrl ?? (returnUrl || ''));

      handleAxiosError(error, customMessageErrorAlert);
    },
    onSettled: () => {
      setIsGlobalLoading(false);
    },
  });
};

export const useUpdateRadioBase = ({
  navigate,
  enableRedirect = true,
  returnUrl,
  returnErrorUrl,
  enableErrorRedirect = false,
  customMessageErrorAlert,
  enableSuccessAlert = true,
  customSuccessMessage,
  onSuccess
}: UseMutationsParams & { onSuccess?: () => void }) => { // Update this line
  const queryClient = useQueryClient();
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  const setAlert = useUiStore.getState().setAlert;

  return useMutation({
    mutationFn: updateRadioBase,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['Radio base'] });
      enableRedirect && navigate && returnUrl && navigate(returnUrl);

      enableSuccessAlert &&
        setAlert &&
        setAlert(
          true,
          customSuccessMessage ?? 'Radio base actualizada correctamente',
          AlertSeverityEnum.success
        );

      onSuccess && onSuccess(); // Add this line
    },
    onError: error => {
      enableErrorRedirect &&
        navigate &&
        returnUrl &&
        navigate(returnErrorUrl ?? (returnUrl || ''));

      handleAxiosError(error, customMessageErrorAlert);
    },
    onSettled: () => {
      setIsGlobalLoading(false);
    },
  });
};

///* axios
export type GetRadioBaseParams = Partial<RadioBase> & {
  page?: number;
  page_size?: number;
  codigo?: string;
  nombre?: string;
};

export type CreateRadioBaseParams = Omit<RadioBase, 'id_radiobase'>;
export type UpdateRadioBaseParams = {
  id: string;
  data: CreateRadioBaseParams;
};

export const getRadioBases = async (params?: GetRadioBaseParams) => {
  const queryParams = getUrlParamsFetch({ ...params });
  return get<RadioBasePagingRes>(`${urlBackend}api/v1/radiobase/?${queryParams}`, true);
};

export const getRadioBase = async (id: string) => {
  const setAlert = useUiStore.getState().setAlert;
  const res = await get<RadioBase>(`${urlBackend}api/v1/radiobase/${id}/`, true);

  res.status === 'error' && setAlert(true, res.message, res.status);
  return res;
};

export const createRadioBase = async (data: CreateRadioBaseParams) => {
  const setAlert = useUiStore.getState().setAlert;
  const res = await post<RadioBase>(`${urlBackend}api/v1/radiobase/`, data, true);

  setAlert(true, res.message, res.status);
  return res;
};

export const updateRadioBase = async ({ id, data }: UpdateRadioBaseParams) => {
  const setAlert = useUiStore.getState().setAlert;
  const res = await put<RadioBase>(`${urlBackend}api/v1/radiobase/${id}/`, data, true);
  setAlert(true, res.message, res.status);
  return res;
};

/***
 * Importar Excel
 * 
*/
export const useFetchImportarExcelRadioBase = () => {
  const queryClient = useQueryClient();

  const setAlert = useUiStore.getState().setAlert;
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;


  return useMutation({

    mutationFn: createRadioBaseUploadExcel,
    onSuccess: data => {
      queryClient.invalidateQueries({ queryKey: ['radiobase-create-array'] });
      setAlert(true, data.message, data.status);
    },

    onError: (error => {
      if (isAxiosError(error)) {
        const respAxiosData = error.response?.data || {};
        if (respAxiosData.code == 400)
          return setAlert(true, `Se ha detectado un código duplicado. Por favor, corrige el archivo y vuelve a subirlo.`, AlertSeverityEnum.error)
      }
      handleAxiosError(error);
    }),
    onSettled: () => {
      setIsGlobalLoading(false);

    }
  });
};

export const createRadioBaseUploadExcel = async (data: { radiobases: any[] }) => {
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  setIsGlobalLoading(true)
  const res = await post<RadioBase>(`${urlBackend}api/v1/radiobase-create-array/`, data, true);
  return res;
};